import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MilkBottleModel } from 'src/app/models/milk.model';
import { PatientModel } from 'src/app/models/patient.model';
import { PrinterModel } from 'src/app/models/printer.model';
import { PrinterService } from 'src/app/services/printer.service';
import { StorageService } from 'src/app/services/storage.service';
import { BaseService } from 'src/app/services/base.service';
import * as _ from 'lodash';
import { getPatientsFromMilks } from '../../utils/milk-label.util';
import { NavigationExtras } from '@angular/router';
import { MilkBottlePrintModel } from '../../models/print-preview.model';
import { getPrintDataByMode } from './kiosk-print-label.util';

export interface PrintLabelInputs {
	patients?: PatientModel[];
	milkBottles?: MilkBottleModel[];
}

export type MilkLabelType = 'milkbottle';

export type PrintedMilkBottlesEvent = {
	type: 'milkbottle';
	patients?: PatientModel[];
	milkBottles: MilkBottleModel[];
};

export type PrintedLabelsEvent = PrintedMilkBottlesEvent;

@Component({
	selector: 'app-kiosk-print-label',
	templateUrl: './kiosk-print-label.component.html',
	styleUrls: ['./kiosk-print-label.component.scss'],
})
export class KioskPrintLabelComponent {
	@Input() selectedPrinter: PrinterModel;
	@Input() isScanOrdered = false;
	@Input() patients: PatientModel[];
	@Input() labelQuantity = 0;
	@Output() printed = new EventEmitter<PrintedLabelsEvent>();
	@Output() cancelModal = new EventEmitter();

	protected readonly StorageService = StorageService;
	private _milkBottles: MilkBottleModel[] = [];


	/**
	 * Returns milk bottles in ascending bottle number order.
	 */
	get milkBottles() {
		return this.isScanOrdered
			? this._milkBottles
			: _.chain(this._milkBottles)
					.sortBy((b) => b.bottleNumber)
					.value();
	}

	/**
	 * Found a quirk where if items are pushed to the array instead of the entire
	 * array being replaced, the `set milkBottles` doesn't trigger because it is
	 * technically already set.
	 *
	 * e.g.
	 * triggers = milkBottles = [...];
	 * updates but doesn't trigger = milkBottles.push({...});
	 */
	@Input()
	set milkBottles(value: MilkBottleModel[]) {
		if (!value || value.length === 0) {
			return;
		}

		this._milkBottles = value;
		this.patients = getPatientsFromMilks(this._milkBottles);
	}

	constructor(
		public printerService: PrinterService,
		public base: BaseService,
	) {}

	/**
	 * Get ZPL printer string
	 */
	getAllPrintData(params: {
		milkBottles: MilkBottleModel[];
		printer: PrinterModel;
	}): string {
		const milkBottles = this.base.labelService.createMilkBottleLabels({
			milkBottles: params.milkBottles,
			printer: params.printer,
		});

		return milkBottles;
	}

	closeModal() {
		this.cancelModal.emit();
	}

	async handlePrint(mode: string) {
		const data = getPrintDataByMode({
			mode,
			milkBottles: this.milkBottles,
		});

		if (this.selectedPrinter.isVirtual) {
			this.goToPrintPreview(data);
			this.printed.emit();
			return;
		}

		try {
			const printData = this.getAllPrintData({
				...data,
				printer: this.selectedPrinter,
			});

			await this.base.presentLoading('Printing');
			await this.printerService.print(this.selectedPrinter, printData);
		} catch (error) {
			console.error(error);
			return;
		} finally {
			await this.base.dismissLoading();
		}

		this.printed.emit();
	}

	async goToPrintPreview(data: {
		milkBottles: MilkBottleModel[];
	}) {
		const navigationExtras: NavigationExtras = {
			state: {
				milkBottles: data.milkBottles.map((x) => new MilkBottlePrintModel(x)),
			},
		};

		StorageService.printPreview = navigationExtras;
		window.open('print-preview');
	}
}
